import { IonPopover } from '@ionic/angular';

export function getPopoverHostElement(popover?: IonPopover): HTMLElement | null {
  return (popover as unknown as { el?: HTMLElement } | undefined)?.el ?? null;
}

export function getDeepActiveElement(): Element | null {
  let activeElement = document.activeElement;

  while (activeElement?.shadowRoot?.activeElement) {
    activeElement = activeElement.shadowRoot.activeElement;
  }

  return activeElement;
}

function blurElement(value?: Element | null): void {
  (value as HTMLElement | null)?.blur?.();
}

function blurButtonNative(element?: HTMLElement | null): void {
  const ionButton = element?.closest?.('ion-button') as HTMLElement | null;
  const nativeButton = ionButton?.shadowRoot?.querySelector('button') as HTMLElement | null;
  blurElement(nativeButton);
}

export function blurActiveOverlayFocus(
  event?: Event,
  triggerElement?: HTMLElement | null,
  activeElementResolver: () => Element | null = getDeepActiveElement
): void {
  const active = activeElementResolver() as HTMLElement | null;
  blurElement(active);
  blurButtonNative(active);

  const target = event?.target as HTMLElement | null;
  blurElement(target);
  blurButtonNative(target);
  blurElement(triggerElement);
  blurButtonNative(triggerElement);

  if (typeof requestAnimationFrame !== 'function') {
    return;
  }

  requestAnimationFrame(() => {
    const frameActive = activeElementResolver() as HTMLElement | null;
    blurElement(frameActive);
    blurButtonNative(frameActive);
    blurElement(triggerElement);
    blurButtonNative(triggerElement);
  });
}

export class PopoverBackdropGuard {
  private cleanup?: () => void;

  constructor(
    private readonly getHostElement: () => HTMLElement | null,
    private readonly onRelease: () => void
  ) {}

  attach(): void {
    this.detach();

    const backdrop = this.getBackdropElement();

    if (!backdrop) {
      return;
    }

    const releaseBackdropFocus = () => {
      this.blurBackdropElement(backdrop);
      this.onRelease();
    };

    backdrop.addEventListener('focus', releaseBackdropFocus);
    backdrop.addEventListener('pointerdown', releaseBackdropFocus);
    this.cleanup = () => {
      backdrop.removeEventListener('focus', releaseBackdropFocus);
      backdrop.removeEventListener('pointerdown', releaseBackdropFocus);
    };
  }

  blurBackdrop(): void {
    this.blurBackdropElement(this.getBackdropElement());
  }

  detach(): void {
    this.cleanup?.();
    this.cleanup = undefined;
  }

  private getBackdropElement(): HTMLElement | null {
    return this.getHostElement()?.shadowRoot?.querySelector('ion-backdrop') as HTMLElement | null;
  }

  private blurBackdropElement(backdrop: HTMLElement | null): void {
    backdrop?.blur();
    if (typeof requestAnimationFrame !== 'function') {
      return;
    }

    requestAnimationFrame(() => backdrop?.blur());
  }
}
